/**
 * Device Filter Utilities
 *
 * Provides filter logic functions for the 5-section filter architecture:
 * - Section 1: Maintenance & Compliance Status (OR logic)
 * - Section 2: Warranty & Contract Coverage (OR logic)
 * - Section 3: Device Characteristics (AND logic)
 * - Section 4: Issues & Problems (OR logic)
 * - Section 5: Search & Custom Dates (AND logic)
 */

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const DAYS_UNTIL_EXPIRY_THRESHOLD = 30;

// ═══════════════════════════════════════════════════════════════════
// STATUS DETERMINATION FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Determine PPM (Preventive Maintenance) status for a device
 *
 * @param {Object} device - Device object
 * @returns {string} - One of: 'Overdue', 'DueSoon', 'Current', 'NotSet'
 */
export const getPpmStatus = (device) => {
  if (!device.ppm_next_due_date) return 'NotSet';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = new Date(device.ppm_next_due_date);
  dueDate.setHours(0, 0, 0, 0);

  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  if (daysUntilDue < 0) return 'Overdue';
  if (daysUntilDue <= DAYS_UNTIL_EXPIRY_THRESHOLD) return 'DueSoon';
  return 'Current';
};

/**
 * Determine Calibration status for a device
 *
 * @param {Object} device - Device object
 * @returns {string} - One of: 'Overdue', 'DueSoon', 'Current', 'NotRequired'
 */
export const getCalibrationStatus = (device) => {
  if (device.calibration_required !== 'Yes') return 'NotRequired';
  if (!device.calibration_next_due_date) return 'NotSet';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = new Date(device.calibration_next_due_date);
  dueDate.setHours(0, 0, 0, 0);

  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  if (daysUntilDue < 0) return 'Overdue';
  if (daysUntilDue <= DAYS_UNTIL_EXPIRY_THRESHOLD) return 'DueSoon';
  return 'Current';
};

/**
 * Determine Compliance status for a device
 *
 * @param {Object} device - Device object
 * @returns {string} - One of: 'Overdue', 'DueSoon', 'Current', 'NotRequired'
 */
export const getComplianceStatus = (device) => {
  if (device.compliance_required !== 'Yes') return 'NotRequired';
  if (!device.compliance_next_due_date) return 'NotSet';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = new Date(device.compliance_next_due_date);
  dueDate.setHours(0, 0, 0, 0);

  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  if (daysUntilDue < 0) return 'Overdue';
  if (daysUntilDue <= DAYS_UNTIL_EXPIRY_THRESHOLD) return 'DueSoon';
  return 'Current';
};

/**
 * Determine Warranty coverage status for a device
 *
 * @param {Object} device - Device object
 * @returns {string} - One of: 'Expired', 'ExpiringWithin30d', 'Current', 'NotCovered'
 */
export const getWarrantyStatus = (device) => {
  if (!device.warranty_expiry) return 'NotCovered';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiryDate = new Date(device.warranty_expiry);
  expiryDate.setHours(0, 0, 0, 0);

  const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) return 'Expired';
  if (daysUntilExpiry <= DAYS_UNTIL_EXPIRY_THRESHOLD) return 'ExpiringWithin30d';
  return 'Current';
};

/**
 * Determine Contract coverage status for a device
 *
 * @param {Object} device - Device object
 * @returns {string} - One of: 'Expired', 'ExpiringWithin30d', 'Current', 'NotCovered'
 */
export const getContractStatus = (device) => {
  if (!device.contract_expiry) return 'NotCovered';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiryDate = new Date(device.contract_expiry);
  expiryDate.setHours(0, 0, 0, 0);

  const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) return 'Expired';
  if (daysUntilExpiry <= DAYS_UNTIL_EXPIRY_THRESHOLD) return 'ExpiringWithin30d';
  return 'Current';
};

const formatCoverageDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Coverage status for the device's active service contract type.
 *
 * @param {Object} device - Device object
 * @returns {string} - One of: 'Expired', 'ExpiringWithin30d', 'Current', 'NotCovered', 'InHouse'
 */
export const getDeviceContractCoverageStatus = (device) => {
  if (!device?.service_contract_type) return 'NotCovered';

  const type = device.service_contract_type;
  if (type === 'In-house') return 'InHouse';
  if (type === 'Warranty') return getWarrantyStatus(device);
  if (type === 'LAMC' || type === 'CAMC') return getContractStatus(device);

  const warrantyStatus = getWarrantyStatus(device);
  if (warrantyStatus !== 'NotCovered') return warrantyStatus;
  return getContractStatus(device);
};

/**
 * Human-readable contract validity for display next to contract type.
 *
 * @param {Object} device - Device object
 * @returns {string|null}
 */
export const getDeviceContractValidityText = (device) => {
  if (!device?.service_contract_type) return null;

  const type = device.service_contract_type;
  if (type === 'Warranty') {
    if (!device.warranty_expiry) return 'Expiry not set';
    const expiry = formatCoverageDate(device.warranty_expiry);
    if (getWarrantyStatus(device) === 'Expired') {
      return expiry;
    }
    return `Valid until ${expiry}`;
  }

  if (type === 'LAMC' || type === 'CAMC') {
    if (!device.contract_expiry) return 'Expiry not set';
    const expiry = formatCoverageDate(device.contract_expiry);
    if (getContractStatus(device) === 'Expired') {
      return expiry;
    }
    return `Valid until ${expiry}`;
  }

  if (type === 'In-house') {
    return 'In-house maintenance';
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════════
// SECTION 1: MAINTENANCE & COMPLIANCE STATUS (OR LOGIC)
// ═══════════════════════════════════════════════════════════════════

/**
 * Check if device matches Section 1 filters (Maintenance & Compliance Status)
 * Uses OR logic: device matches if ANY condition is true
 *
 * @param {Object} device - Device object
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if device matches filter criteria
 */
export const matchesMaintStatusFilters = (device, filters) => {
  const { ppmStatus = [], calibrationStatus = [], complianceStatus = [] } = filters;

  // If no filters selected, all devices match
  if (ppmStatus.length === 0 && calibrationStatus.length === 0 && complianceStatus.length === 0) {
    return true;
  }

  // Device matches if ANY selected condition is true
  let matches = false;

  // Check PPM status
  if (ppmStatus.length > 0) {
    const devicePpmStatus = getPpmStatus(device);
    if (ppmStatus.includes(devicePpmStatus)) {
      matches = true;
    }
  }

  // Check Calibration status
  if (calibrationStatus.length > 0) {
    const deviceCalStatus = getCalibrationStatus(device);
    if (calibrationStatus.includes(deviceCalStatus)) {
      matches = true;
    }
  }

  // Check Compliance status
  if (complianceStatus.length > 0) {
    const deviceCompStatus = getComplianceStatus(device);
    if (complianceStatus.includes(deviceCompStatus)) {
      matches = true;
    }
  }

  return matches;
};

// ═══════════════════════════════════════════════════════════════════
// SECTION 2: WARRANTY & CONTRACT COVERAGE (OR LOGIC)
// ═══════════════════════════════════════════════════════════════════

/**
 * Check if device matches Section 2 filters (Warranty & Contract Coverage)
 * Uses OR logic: device matches if ANY condition is true
 *
 * @param {Object} device - Device object
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if device matches filter criteria
 */
export const matchesCoverageFilters = (device, filters) => {
  const { warrantyStatus = [], contractStatus = [] } = filters;

  // If no filters selected, all devices match
  if (warrantyStatus.length === 0 && contractStatus.length === 0) {
    return true;
  }

  // Device matches if ANY selected condition is true
  let matches = false;

  // Check Warranty status
  if (warrantyStatus.length > 0) {
    const deviceWarrantyStatus = getWarrantyStatus(device);
    if (warrantyStatus.includes(deviceWarrantyStatus)) {
      matches = true;
    }
  }

  // Check Contract status
  if (contractStatus.length > 0) {
    const deviceContractStatus = getContractStatus(device);
    if (contractStatus.includes(deviceContractStatus)) {
      matches = true;
    }
  }

  return matches;
};

// ═══════════════════════════════════════════════════════════════════
// SECTION 3: DEVICE CHARACTERISTICS (AND LOGIC)
// ═══════════════════════════════════════════════════════════════════

/**
 * Check if device matches Section 3 filters (Device Characteristics)
 * Uses AND logic: device must match ALL selected characteristics
 *
 * @param {Object} device - Device object
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if device matches all filter criteria
 */
export const matchesCharacteristicFilters = (device, filters) => {
  const {
    departments = [],
    criticality = [],
    serviceContractTypes = [],
    deviceStatus = [],
    complianceRequired = '',
    calibrationRequired = '',
    productCostFrom = '',
    productCostTo = ''
  } = filters;

  // Department filter (AND logic)
  if (departments.length > 0 && !departments.includes(device.department)) {
    return false;
  }

  // Criticality filter (AND logic)
  if (criticality.length > 0 && !criticality.includes(device.criticality)) {
    return false;
  }

  // Service Contract Type filter (AND logic)
  if (serviceContractTypes.length > 0 && !serviceContractTypes.includes(device.service_contract_type)) {
    return false;
  }

  // Device status filter (narrows list-visible statuses)
  if (deviceStatus.length > 0 && !deviceStatus.includes(device.device_status)) {
    return false;
  }

  // Compliance Required filter (AND logic)
  if (complianceRequired !== '' && device.compliance_required !== complianceRequired) {
    return false;
  }

  // Calibration Required filter (AND logic)
  if (calibrationRequired !== '' && device.calibration_required !== calibrationRequired) {
    return false;
  }

  // Product Cost Range filter (AND logic)
  if (productCostFrom !== '') {
    const costFrom = parseFloat(productCostFrom);
    const deviceCost = parseFloat(device.product_cost) || 0;
    if (deviceCost < costFrom) {
      return false;
    }
  }

  if (productCostTo !== '') {
    const costTo = parseFloat(productCostTo);
    const deviceCost = parseFloat(device.product_cost) || 0;
    if (deviceCost > costTo) {
      return false;
    }
  }

  return true;
};

// ═══════════════════════════════════════════════════════════════════
// SECTION 4: ISSUES & PROBLEMS (OR LOGIC)
// ═══════════════════════════════════════════════════════════════════

/**
 * Check if device matches Section 4 filters (Issues & Problems)
 *
 * @param {Object} device - Device object
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if device matches filter criteria
 */
export const matchesIssueFilters = (device, filters) => {
  const { hasActiveComplaints = false, impactsAvailability = '' } = filters;

  if (impactsAvailability === 'yes' && !device.has_availability_impact) {
    return false;
  }

  if (impactsAvailability === 'no' || impactsAvailability === 'pending') {
    // Client-side path: rely on backend filter when using API; skip strict local check.
    return true;
  }

  if (!hasActiveComplaints) {
    return true; // Filter not active, all devices match
  }

  return device.active_complaint_count > 0;
};

// ═══════════════════════════════════════════════════════════════════
// SECTION 5: SEARCH & CUSTOM DATES (AND LOGIC)
// ═══════════════════════════════════════════════════════════════════

/**
 * Check if device matches Section 5 filters (Search & Custom Dates)
 * Uses AND logic for search terms and date ranges
 *
 * @param {Object} device - Device object
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if device matches all filter criteria
 */
export const matchesSearchFilters = (device, filters) => {
  const {
    search = '',
    installationDateFrom = '',
    installationDateTo = ''
  } = filters;

  // Free-text search (AND logic - all terms must match)
  if (search.trim()) {
    const searchTerms = search.toLowerCase().split(/\s+/).filter(t => t);
    const searchableText = [
      device.device_identifier || '',
      device.name || '',
      device.serial_number || '',
      device.model || '',
      device.manufacturer || '',
      device.physical_location || ''
    ].join(' ').toLowerCase();

    const allTermsMatch = searchTerms.every(term => searchableText.includes(term));
    if (!allTermsMatch) {
      return false;
    }
  }

  // Installation Date Range filters (AND logic)
  if (installationDateFrom) {
    const fromDate = new Date(installationDateFrom);
    fromDate.setHours(0, 0, 0, 0);
    const deviceDate = new Date(device.installation_date);
    deviceDate.setHours(0, 0, 0, 0);
    if (deviceDate < fromDate) {
      return false;
    }
  }

  if (installationDateTo) {
    const toDate = new Date(installationDateTo);
    toDate.setHours(23, 59, 59, 999);
    const deviceDate = new Date(device.installation_date);
    deviceDate.setHours(0, 0, 0, 0);
    if (deviceDate > toDate) {
      return false;
    }
  }

  return true;
};

// ═══════════════════════════════════════════════════════════════════
// MAIN FILTER APPLICATION FUNCTION
// ═══════════════════════════════════════════════════════════════════

/**
 * Apply all filters to a device list
 *
 * Logic Summary:
 * - Sections 1, 2, 4: OR logic (device matches if ANY condition met)
 * - Section 3, 5: AND logic (device must match ALL conditions)
 *
 * @param {Array} devices - Array of device objects
 * @param {Object} filters - Filter state object with all 5 sections
 * @returns {Array} - Filtered array of devices
 */
export const applyNewFilters = (devices, filters) => {
  return devices.filter(device => {
    // SECTION 1: Maintenance & Compliance Status (OR)
    if (!matchesMaintStatusFilters(device, filters)) {
      return false;
    }

    // SECTION 2: Warranty & Contract Coverage (OR)
    if (!matchesCoverageFilters(device, filters)) {
      return false;
    }

    // SECTION 3: Device Characteristics (AND)
    if (!matchesCharacteristicFilters(device, filters)) {
      return false;
    }

    // SECTION 4: Issues & Problems (OR)
    if (!matchesIssueFilters(device, filters)) {
      return false;
    }

    // SECTION 5: Search & Custom Dates (AND)
    if (!matchesSearchFilters(device, filters)) {
      return false;
    }

    return true;
  });
};

// ═══════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS FOR UI - CHECK WHICH FILTERS ARE ACTIVE
// ═══════════════════════════════════════════════════════════════════

/**
 * Check if Section 1 (Maintenance & Compliance Status) has active filters
 *
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if any maintenance filter is active
 */
export const hasActiveMaintenanceFilters = (filters) => {
  return (
    (filters.ppmStatus && filters.ppmStatus.length > 0) ||
    (filters.calibrationStatus && filters.calibrationStatus.length > 0) ||
    (filters.complianceStatus && filters.complianceStatus.length > 0)
  );
};

/**
 * Check if Section 2 (Warranty & Contract Coverage) has active filters
 *
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if any coverage filter is active
 */
export const hasActiveCoverageFilters = (filters) => {
  return (
    (filters.warrantyStatus && filters.warrantyStatus.length > 0) ||
    (filters.contractStatus && filters.contractStatus.length > 0)
  );
};

/**
 * Check if Section 3 (Device Characteristics) has active filters
 *
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if any characteristic filter is active
 */
export const hasActiveCharacteristicFilters = (filters) => {
  return (
    (filters.departments && filters.departments.length > 0) ||
    (filters.criticality && filters.criticality.length > 0) ||
    (filters.serviceContractTypes && filters.serviceContractTypes.length > 0) ||
    (filters.deviceStatus && filters.deviceStatus.length > 0) ||
    (filters.complianceRequired && filters.complianceRequired !== '') ||
    (filters.calibrationRequired && filters.calibrationRequired !== '') ||
    (filters.productCostFrom && filters.productCostFrom !== '') ||
    (filters.productCostTo && filters.productCostTo !== '')
  );
};

/**
 * Check if Section 4 (Issues & Problems) has active filters
 *
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if any issue filter is active
 */
export const hasActiveIssueFilters = (filters) => {
  return filters.hasActiveComplaints === true;
};

/**
 * Check if Section 5 custom date fields have active filters.
 * Free-text search is intentionally excluded — it is not an Active Filter chip.
 *
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if installation date filters are active
 */
export const hasActiveSearchFilters = (filters) => {
  return (
    (filters.installationDateFrom && filters.installationDateFrom !== '') ||
    (filters.installationDateTo && filters.installationDateTo !== '')
  );
};

/**
 * Check if ANY filters are currently applied
 *
 * @param {Object} filters - Filter state
 * @returns {boolean} - True if any filters are active
 */
export const hasAnyNewFiltersApplied = (filters) => {
  return (
    hasActiveMaintenanceFilters(filters) ||
    hasActiveCoverageFilters(filters) ||
    hasActiveCharacteristicFilters(filters) ||
    hasActiveIssueFilters(filters) ||
    hasActiveSearchFilters(filters)
  );
};

/**
 * Whether a filter key is active (for pill display and activation order tracking).
 */
export const isDeviceFilterKeyActive = (filtersObj, key) => {
  switch (key) {
    case 'search':
      // Free-text search is not an Active Filter (search box owns clear UX)
      return false;
    case 'departments':
      return (filtersObj.departments || []).length > 0;
    case 'criticality':
      return (filtersObj.criticality || []).length > 0;
    case 'locations':
      return (filtersObj.locations || []).length > 0;
    case 'ppmStatus':
      return (filtersObj.ppmStatus || []).length > 0;
    case 'calibrationStatus':
      return (filtersObj.calibrationStatus || []).length > 0;
    case 'complianceStatus':
      return (filtersObj.complianceStatus || []).length > 0;
    case 'coverageStatus':
      return (filtersObj.coverageStatus || []).length > 0;
    case 'serviceContractTypes':
      return (filtersObj.serviceContractTypes || []).length > 0;
    case 'deviceStatus':
      return (filtersObj.deviceStatus || []).length > 0;
    case 'complianceRequired':
      return !!filtersObj.complianceRequired;
    case 'calibrationRequired':
      return !!filtersObj.calibrationRequired;
    case 'productCostRange':
      return !!(filtersObj.productCostFrom || filtersObj.productCostTo);
    case 'installationDateRange':
      return !!(filtersObj.installationDateFrom || filtersObj.installationDateTo);
    case 'warrantyDateRange':
      return !!(filtersObj.warrantyDateFrom || filtersObj.warrantyDateTo);
    case 'contractDateRange':
      return !!(filtersObj.contractDateFrom || filtersObj.contractDateTo);
    case 'hasActiveComplaints':
      return !!filtersObj.hasActiveComplaints;
    case 'hasNoActiveComplaints':
      return !!filtersObj.hasNoActiveComplaints;
    case 'impactsAvailability':
      return !!(filtersObj.impactsAvailability && filtersObj.impactsAvailability !== '');
    default:
      return false;
  }
};
